import { createSelector } from '@ngrx/store';

import { zonesFeature } from '../core/zones/zones.reducer';
import {
  brandingFeature,
  createCheckMuliplePermissionsSelector,
  createCheckSinglePermissionSelector,
} from '../state/branding.store';

// Requirement: the server ANDs `bluesage.query` and `bluesage.terminal` on
// every card rail, so a role holding only the read cannot take money.
//
// Requirement: the permissions only, never the zone's card config — settling or
// releasing a hold must stay possible in a zone whose processor has since been
// switched off, because the money is already on a customer's card.
export const selectMayTakeBlueSageCardPayment = createSelector(
  brandingFeature.selectBranding,
  zonesFeature.selectCurrentAppZone,
  createCheckMuliplePermissionsSelector([
    { permission: 'bluesage.query' },
    { permission: 'bluesage.terminal' },
  ]),
  (branding, currentZone, blueSagePermissions) => {
    const brandingMatchesZone = Boolean(
      branding?.zoneId && currentZone?.id && branding.zoneId === currentZone.id,
    );

    return brandingMatchesZone && blueSagePermissions.every(Boolean);
  },
);

// Requirement: releasing a hold travels the refund path, so it takes
// `transactions.refundTransaction` and not `bluesage.terminal` — a rep or crew
// lead may create a hold and may not release one.
export const selectMayRefundTransaction = createSelector(
  brandingFeature.selectBranding,
  zonesFeature.selectCurrentAppZone,
  createCheckSinglePermissionSelector({ permission: 'transactions.refundTransaction' }),
  (branding, currentZone, canRefund) => {
    const brandingMatchesZone = Boolean(
      branding?.zoneId && currentZone?.id && branding.zoneId === currentZone.id,
    );

    return brandingMatchesZone && Boolean(canRefund);
  },
);

// Requirement: one zone must never be answered for by the zone the operator
// just left. `zoneChanged` fires before branding refetches and Branding Loading
// keeps the previous zone's configs and permissions, so the answer holds only
// while `branding.zoneId` matches the current zone; either id absent fails
// closed.
export const selectBlueSageCardRailsAvailable = createSelector(
  brandingFeature.selectConfigs,
  brandingFeature.selectBranding,
  zonesFeature.selectCurrentAppZone,
  selectMayTakeBlueSageCardPayment,
  (configs, branding, currentZone, mayTakeCardPayment) => {
    const brandingMatchesZone = Boolean(
      branding?.zoneId && currentZone?.id && branding.zoneId === currentZone.id,
    );
    if (!brandingMatchesZone) { return false; }

    const zoneCanTakeCards = Boolean(
      configs?.PAYMENT__TYPE_BLUESAGE__PAYLINK?.enabled && configs?.BLUESAGE_LOCATIONID,
    );

    return zoneCanTakeCards && mayTakeCardPayment;
  },
);
